import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { articles } from "@/mocks/categories";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.blog";

export default function GenericArticle() {
  const location = useLocation();
  const [openFaqs, setOpenFaqs] = useState<number[]>([]);
  const slug = location.pathname;
  const articleMeta = articles.find((a) => a.slug === slug);
  const articleUrl = `${siteUrl}${slug}`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaqs((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  if (!articleMeta) {
    return (
      <main>
        <Navbar />
        <section className="pt-32 pb-16 text-center bg-background-50">
          <h1 className="text-2xl font-bold text-foreground-900">Article not found</h1>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-accent-600 hover:text-accent-700 font-medium">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  const faqs = [
    {
      q: "What exactly is acbuyspreadsheet?",
      a: "acbuyspreadsheet is an innovative spreadsheet-based system designed to simplify product comparison, purchasing workflows, and data-driven decision-making. It comes with pre-built templates for 15 product categories including Shoes, Hoodies, T-Shirts, Jackets, Bags, Accessories, and more.",
    },
    {
      q: "How does acbuyspreadsheet help with buying decisions?",
      a: "acbuyspreadsheet provides structured comparison templates that allow you to evaluate multiple products side by side. It automatically calculates price-per-unit metrics, tracks historical price data, flags cost-effective options, and generates visual summaries so you can make confident, informed purchases.",
    },
    {
      q: "Is acbuyspreadsheet suitable for beginners?",
      a: "Absolutely. acbuyspreadsheet is designed with a beginner-friendly approach. The interface is clean and intuitive, with pre-built templates and guided workflows. You do not need any advanced spreadsheet knowledge to start using it effectively.",
    },
    {
      q: "What categories does acbuyspreadsheet support?",
      a: "acbuyspreadsheet supports 15 product categories including Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category has a dedicated comparison template.",
    },
    {
      q: "Can I access acbuyspreadsheet on my mobile phone?",
      a: "Yes, acbuyspreadsheet features a fully responsive design that works seamlessly on mobile devices. The premium plan also includes offline access, so you can review comparisons even without an internet connection.",
    },
    {
      q: "Where can I get started with acbuyspreadsheet?",
      a: "You can start using acbuyspreadsheet by visiting the official platform at kaspreadsheet.com. The free tier gives you immediate access to all core comparison tools and 50+ templates with no credit card required.",
    },
  ];

  const faqLD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const breadcrumbLD = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${siteUrl}/` },
      { "@type": "ListItem", "position": 3, "name": articleMeta.title, "item": articleUrl },
    ],
  };

  const articleLD = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": articleMeta.title,
    "description": articleMeta.excerpt,
    "author": { "@type": "Organization", "name": "acbuyspreadsheet", "url": siteUrl },
    "publisher": { "@type": "Organization", "name": "acbuyspreadsheet", "url": siteUrl },
    "datePublished": "2026-06-01",
    "dateModified": "2026-06-24",
    "mainEntityOfPage": { "@type": "WebPage", "@id": articleUrl },
    "url": articleUrl,
    "keywords": ["acbuyspreadsheet", "acbuy spreadsheet", "spreadsheet guide", "buying tools", "product comparison"],
  };

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLD) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLD) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLD) }}
      />
      <Navbar />

      <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-background-50">
        <div className="absolute inset-0 overflow-hidden opacity-[0.05]">
          <img src={articleMeta.image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
          <nav className="flex items-center justify-center gap-2 text-xs text-foreground-400 mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors font-medium">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">Blog</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-accent-500 font-medium line-clamp-1">{articleMeta.title.split(":")[0]}</span>
          </nav>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold font-heading text-foreground-900 leading-tight">
            {articleMeta.title}
          </h1>
          <div className="mt-4 flex items-center justify-center gap-3 text-xs text-foreground-400">
            <span>Updated: June 2026</span>
            <span>·</span>
            <span>Expert-reviewed content</span>
          </div>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <span className="px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-medium">acbuy spreadsheet</span>
            <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">guide</span>
            <span className="px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium">tips</span>
          </div>
        </div>
      </section>

      <article className="py-10 md:py-14 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-foreground-700 leading-relaxed text-sm md:text-base space-y-6">
            <p>
              {articleMeta.excerpt} This comprehensive guide dives deep into everything you need to know, brought to you by the{' '}
              <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">acbuyspreadsheet homepage</Link>.
            </p>

            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mt-10 mb-4">
              Why acbuyspreadsheet Matters in 2026
            </h2>
            <p>
              In today's fast-paced online marketplace, acbuyspreadsheet has emerged as an essential tool for anyone who makes regular purchasing decisions. The platform combines the flexibility of spreadsheet analysis with specialized buying features that automate the most time-consuming parts of product comparison. Whether you are evaluating Shoes, comparing Hoodies, analyzing Bags and Accessories, or tracking prices across any of the 15 supported categories, acbuyspreadsheet streamlines the entire process from research to decision.
            </p>

            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mt-10 mb-4">
              How acbuyspreadsheet Delivers Real Results
            </h2>
            <p>
              The core advantage of acbuyspreadsheet is its purpose-built design. Unlike generic spreadsheet tools that require hours of manual setup, acbuyspreadsheet arrives ready to use with pre-configured templates for every product category. Import data from multiple sources, let the auto-compare engine align features side by side, and watch as the price-per-unit calculator reveals the true best value options automatically. The result is faster, smarter, and more confident buying decisions every time.
            </p>

            <div className="overflow-x-auto rounded-xl border border-background-200 my-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-accent-50">
                    <th className="text-left p-3 md:p-4 font-semibold text-foreground-800">Feature</th>
                    <th className="text-left p-3 md:p-4 font-semibold text-accent-700">acbuyspreadsheet</th>
                    <th className="text-left p-3 md:p-4 font-semibold text-foreground-600">Other Tools</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: "Setup Time", acbuy: "Under 2 minutes", competitor: "30+ minutes" },
                    { feature: "Category Templates", acbuy: "50+ pre-built", competitor: "Build from scratch" },
                    { feature: "Price Analysis", acbuy: "Automatic", competitor: "Manual calculation" },
                    { feature: "Cross-device Sync", acbuy: "Cloud sync", competitor: "File transfer" },
                    { feature: "Learning Curve", acbuy: "15 minutes", competitor: "Hours" },
                    { feature: "Data Import", acbuy: "Multi-source auto", competitor: "Copy-paste only" },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-background-50" : "bg-background-100"}>
                      <td className="p-3 md:p-4 font-medium text-foreground-800">{row.feature}</td>
                      <td className="p-3 md:p-4 text-accent-700 font-medium">{row.acbuy}</td>
                      <td className="p-3 md:p-4 text-foreground-500">{row.competitor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mt-10 mb-4">
              Getting the Most Out of acbuyspreadsheet
            </h2>
            <p>
              To maximize your acbuyspreadsheet experience, start by selecting the category template that matches your shopping needs. Import product listings using the auto-import feature, review the automatically calculated value metrics, and use the visual dashboard to spot the best options at a glance. Save your comparisons for future reference, and sync your data across all devices so your research is always accessible. Whether you are a casual shopper, a dropshipping entrepreneur, or a procurement professional, acbuyspreadsheet adapts to your workflow.
            </p>

            <div className="my-10 p-6 md:p-8 rounded-2xl bg-accent-50 border border-accent-200 text-center">
              <h3 className="text-lg font-bold font-heading text-foreground-900">Ready to Try acbuyspreadsheet?</h3>
              <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto">
                Experience the power of smarter buying. Visit the official platform and start optimizing your purchasing workflow today.
              </p>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Try acbuyspreadsheet Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            <section className="mt-12">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="rounded-xl border border-background-200 bg-background-50 overflow-hidden">
                    <button
                      onClick={() => toggleFaq(i)}
                      className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-100 transition-colors"
                    >
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <i className={`ri-add-line text-base text-foreground-400 transition-transform flex-shrink-0 ${openFaqs.includes(i) ? "rotate-45" : ""}`}></i>
                    </button>
                    <div className={`faq-answer overflow-hidden ${openFaqs.includes(i) ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-10 pt-8 border-t border-background-200">
              <p className="text-sm text-foreground-500">
                Want to learn more about acbuyspreadsheet?{' '}
                <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">
                  Return to the acbuyspreadsheet homepage
                </Link>
                {' '}for more guides, comparisons, and tools.
              </p>
            </div>
          </div>
        </div>
      </article>

      <section className="py-14 md:py-18 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
            Related acbuyspreadsheet Articles
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
            <Link to="/what-is-acbuyspreadsheet" className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200 hover:border-accent-300 transition-all cursor-pointer">
              <div className="h-36 overflow-hidden bg-background-100">
                <img src="https://readdy.ai/api/search-image?query=Modern%20laptop%20displaying%20clean%20spreadsheet%20interface%20on%20wooden%20desk%20with%20coffee%20cup%2C%20soft%20morning%20light%2C%20productive%20workspace%20scene%2C%20warm%20neutral%20tones%2C%20lifestyle%20tech%20photography&width=600&height=400&seq=article-card-img-01&orientation=landscape" alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-bold text-foreground-800 group-hover:text-accent-600 transition-colors line-clamp-2">What is acbuyspreadsheet? A Complete Guide</h3>
              </div>
            </Link>
            <Link to="/" className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200 hover:border-accent-300 transition-all cursor-pointer">
              <div className="h-36 overflow-hidden bg-background-100">
                <img src="https://readdy.ai/api/search-image?query=Happy%20diverse%20group%20of%20people%20collaborating%20around%20a%20table%20with%20laptops%20and%20notebooks%2C%20bright%20airy%20modern%20office%2C%20genuine%20smiles%2C%20success%20and%20teamwork%20concept%20photography&width=600&height=400&seq=article-card-img-13&orientation=landscape" alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-bold text-foreground-800 group-hover:text-accent-600 transition-colors line-clamp-2">acbuyspreadsheet Homepage: Guides and Tools</h3>
              </div>
            </Link>
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200 hover:border-accent-300 transition-all cursor-pointer">
              <div className="h-36 overflow-hidden bg-background-100">
                <img src="https://readdy.ai/api/search-image?query=Modern%20ecommerce%20workspace%20with%20laptop%20showing%20product%20listings%20and%20shipping%20boxes%20nearby%2C%20clean%20desk%20setup%2C%20natural%20light%2C%20dropshipping%20business%20concept%20photography&width=600&height=400&seq=article-card-img-10&orientation=landscape" alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-bold text-foreground-800 group-hover:text-accent-600 transition-colors line-clamp-2">Visit Official Store: kaspreadsheet.com</h3>
              </div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}